import { Badge, BodyText, Button, Collapsible, Tooltip } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { cn } from "@/lib/utils";
import { useTakeEncounter } from "@/network/useTakeEncounter";
import { Encounter } from "@curai/js-fhir-tools";
import { useParams, useRouter, useSearch } from "@tanstack/react-router";
import {
  CircleSlash,
  ClipboardList,
  Eye,
  History,
  Image,
  Loader,
  MessageSquareMore,
  Notebook,
  Pill,
  Stethoscope,
  TestTube,
  UserRound,
} from "lucide-react";
import { useMemo } from "react";
import { EncounterCardDetails } from "./EncounterCardDetails";
import type { EncountersResponse_Encounter } from "./network/useTimelineEncounters";
import {
  EncountersResponse_EncounterStatus,
  EncountersResponse_ServiceType,
} from "./network/useTimelineEncounters";

const SERVICE_TYPE_BADGES: Record<
  EncountersResponse_ServiceType,
  { label: string; Icon: typeof Pill }
> = {
  [EncountersResponse_ServiceType.GENERAL_PRACTICE]: {
    label: "Async Dx",
    Icon: Stethoscope,
  },
  [EncountersResponse_ServiceType.MEDICATION_REVIEWS]: {
    label: "Rx Renewal",
    Icon: Pill,
  },
  [EncountersResponse_ServiceType.PRESCRIPTIONS]: {
    label: "Async Rx",
    Icon: Pill,
  },
};

interface EncounterCardProps {
  encounter: EncountersResponse_Encounter;
}

export const EncounterCard = ({ encounter }: EncounterCardProps) => {
  const { navigate } = useRouter();
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId: urlEncounterId } = useSearch({
    strict: false,
  });
  // Check if the encounter is a mother encounter. If so then use it's id as the mother encounter id
  const fhirEncounter = new Encounter(encounter.encounter);
  const motherEncounterId: string = fhirEncounter.isMother
    ? (fhirEncounter.id ?? encounter.id)
    : fhirEncounter.motherEncounterId;

  const isCurrentlyViewing = motherEncounterId === urlEncounterId;

  const takeEncounter = useTakeEncounter({
    patientId,
    encounterId: motherEncounterId,
  });

  const badges = useMemo(() => {
    const applicableBadges = [];

    const iconClassName = "w-[11px]";

    if (encounter.isLegacy)
      applicableBadges.push(
        <Badge key="legacy" variant="default">
          <History className={iconClassName} />
          <BodyText size="sm" textColor="secondary">
            Legacy
          </BodyText>
        </Badge>,
      );

    if (encounter.classFhir === "LIVE" && !encounter.serviceType)
      applicableBadges.push(
        <Badge key="live" variant="default">
          <UserRound className={iconClassName} />
          <BodyText size="sm" textColor="secondary">
            Live
          </BodyText>
        </Badge>,
      );
    if (encounter.classFhir === "ASYNC")
      applicableBadges.push(
        <Badge key="async" variant="default">
          <MessageSquareMore className={iconClassName} />
          <BodyText size="sm" textColor="secondary">
            Async
          </BodyText>
        </Badge>,
      );

    if (encounter.serviceType) {
      const serviceBadge = SERVICE_TYPE_BADGES[encounter.serviceType];
      if (serviceBadge) {
        const { label, Icon } = serviceBadge;
        applicableBadges.push(
          <Badge key="service-type" variant="default">
            <Icon className={iconClassName} />
            <BodyText size="sm" textColor="secondary">
              {label}
            </BodyText>
          </Badge>,
        );
      }
    }

    if (encounter.hasFiles)
      applicableBadges.push(
        <Badge key="files" variant="default">
          <Image className={iconClassName} />
          <BodyText size="sm" textColor="secondary">
            Files
          </BodyText>
        </Badge>,
      );
    if (encounter.hasLabs)
      applicableBadges.push(
        <Badge key="labs" variant="default">
          <TestTube className={iconClassName} />
          <BodyText size="sm" textColor="secondary">
            Labs
          </BodyText>
        </Badge>,
      );
    if (encounter.hasPostVisitMsgs)
      applicableBadges.push(
        <Badge key="messages" variant="default">
          <MessageSquareMore className={iconClassName} />
          <BodyText size="sm" textColor="secondary">
            Messages
          </BodyText>
        </Badge>,
      );
    if (encounter.hasRx)
      applicableBadges.push(
        <Badge key="rx" variant="default">
          <Pill className={iconClassName} />
          <BodyText size="sm" textColor="secondary">
            Rx
          </BodyText>
        </Badge>,
      );
    if (encounter.hasAdminNotes)
      applicableBadges.push(
        <Badge key="admin-notes" variant="default">
          <Notebook className={iconClassName} />
          <BodyText size="sm" textColor="secondary">
            Admin Notes
          </BodyText>
        </Badge>,
      );
    if (encounter.hasPostVisitMsgs)
      applicableBadges.push(
        <Badge key="post-visit-messages" variant="default">
          <MessageSquareMore className={iconClassName} />
          <BodyText size="sm" textColor="secondary">
            Post-Visit Messages
          </BodyText>
        </Badge>,
      );
    if (encounter.priorCarePlanCount)
      applicableBadges.push(
        <Badge key="new-care-plan" variant="default">
          <ClipboardList className={iconClassName} />
          <BodyText size="sm" textColor="secondary">
            New Care Plan ({encounter.priorCarePlanCount} Prev)
          </BodyText>
        </Badge>,
      );

    if (
      encounter.encounterStatus ===
      EncountersResponse_EncounterStatus.INCOMPLETE
    ) {
      applicableBadges.push(
        <Badge key="incomplete" variant="warning">
          <CircleSlash className={cn(iconClassName, "text-warning")} />
          <BodyText size="sm" textColor="warning">
            Incomplete
          </BodyText>
        </Badge>,
      );
    }
    // TODO: Change isConcurrent to inChatWithDr or something, but it needs to be updated in the backend
    if (encounter.isConcurrent) {
      applicableBadges.push(
        <Badge key="in-progress" variant="default">
          <Loader className={iconClassName} />
          <BodyText size="sm" textColor="secondary">
            In Progress
          </BodyText>
        </Badge>,
      );
    }

    return applicableBadges;
  }, [encounter]);

  const isAsync = encounter.classFhir === "ASYNC";

  const handleClickTake = async () => {
    await takeEncounter.mutateAsync(false);

    navigate({
      to: "/patient/$patientId/timeline",
      params: {
        patientId,
      },
      search: {
        encounterId: motherEncounterId,
        chatTab: isAsync ? ("async" as const) : undefined,
      },
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      trackEvent(TRACKERS.VIEW_TIMELINE_CARD);
    }
  };

  return (
    <Collapsible
      onOpenChange={handleOpenChange}
      title={encounter.title}
      headerSize="sm"
      statusBadge={
        isCurrentlyViewing ? (
          <Tooltip tooltipContentText="Currently viewing visit">
            <Eye className="h-3 w-3 text-tertiary" />
          </Tooltip>
        ) : undefined
      }
      badgeComponents={badges}
      customActions={[
        encounter.isTakeable && (
          <Button
            key="take"
            size="sm"
            className="leading-tight"
            isLoading={takeEncounter.isPending}
            onClick={handleClickTake}
          >
            Take
          </Button>
        ),
        encounter.isConcurrent && (
          <Button key="taken" size="sm" className="leading-tight" disabled>
            Taken
          </Button>
        ),
      ].filter(Boolean)}
    >
      <div className="pt-2">
        <EncounterCardDetails
          encounterId={encounter.id}
          motherEncounterId={motherEncounterId}
          encounterStatus={encounter.encounterStatus}
          classFhir={encounter.classFhir}
        />
      </div>
    </Collapsible>
  );
};
